import { Sparkles, Eye, Tag } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Categories for grouping optional features in onboarding & settings UI.
 */
export type OptionalFeatureCategory = 'personal-layer' | 'social' | 'discovery';

/**
 * A single opt-in feature module. To add a new opt-in (e.g. dream journal,
 * tarot, biometric layer) just append a new entry to OPTIONAL_FEATURES.
 * No other UI plumbing required.
 */
export interface OptionalFeatureDefinition {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Direct route exposed when feature is enabled (omit if no dedicated page). */
  route?: string;
  category: OptionalFeatureCategory;
  /** Default value when no record exists yet for this user. */
  defaultEnabled: boolean;
  /** React Query keys to invalidate after a write so downstream UIs react. */
  invalidateKeys: (userId: string) => readonly (readonly unknown[])[];
  /** Read current value for an authed user. Returns boolean. */
  read: (userId: string) => Promise<boolean>;
  /** Persist value for an authed user. */
  write: (userId: string, on: boolean) => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Storage adapters
// ─────────────────────────────────────────────────────────────────────────────

async function readPrivacyFlag(
  userId: string,
  field: 'show_resonance_fingerprint' | 'show_type_monogram' | 'show_on_leaderboard',
  fallback: boolean,
): Promise<boolean> {
  const { data } = await supabase
    .from('privacy_settings')
    .select(field)
    .eq('user_id', userId)
    .maybeSingle();
  if (!data) return fallback;
  const v = (data as Record<string, unknown>)[field];
  return typeof v === 'boolean' ? v : fallback;
}

async function writePrivacyFlag(
  userId: string,
  field: 'show_resonance_fingerprint' | 'show_type_monogram' | 'show_on_leaderboard',
  on: boolean,
): Promise<void> {
  // upsert keeps things resilient if no privacy_settings row exists yet.
  const { error } = await supabase
    .from('privacy_settings')
    .upsert({ user_id: userId, [field]: on } as never, { onConflict: 'user_id' });
  if (error) throw error;
}

async function readAstrologyFlag(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from('user_optional_features')
    .select('astrology_enabled')
    .eq('user_id', userId)
    .maybeSingle();
  return !!data?.astrology_enabled;
}

async function writeAstrologyFlag(userId: string, on: boolean): Promise<void> {
  const { error } = await supabase.from('user_optional_features').upsert(
    {
      user_id: userId,
      astrology_enabled: on,
      enabled_at: on ? new Date().toISOString() : null,
    },
    { onConflict: 'user_id' },
  );
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// Registry
// ─────────────────────────────────────────────────────────────────────────────

export const OPTIONAL_FEATURES: readonly OptionalFeatureDefinition[] = [
  {
    id: 'astrology',
    label: 'Astrology',
    description:
      'A birth-chart-based personal layer with a private chart view. Never injected into TPS or matching.',
    icon: Sparkles,
    route: '/astrology',
    category: 'personal-layer',
    defaultEnabled: false,
    invalidateKeys: (userId) => [
      ['astrology-access', userId],
      ['astrology-chart', userId],
    ],
    read: readAstrologyFlag,
    write: writeAstrologyFlag,
  },
  {
    id: 'show_resonance_fingerprint',
    label: 'Resonance fingerprint on posts',
    description:
      'Show your 36-trait spectrum beneath your threads, replies, and insights.',
    icon: Eye,
    category: 'social',
    defaultEnabled: true,
    invalidateKeys: (userId) => [
      ['privacy-resonance-visibility', userId],
      ['privacy-show-fingerprint', userId],
      ['author-resonance', userId],
    ],
    read: (uid) => readPrivacyFlag(uid, 'show_resonance_fingerprint', true),
    write: (uid, on) => writePrivacyFlag(uid, 'show_resonance_fingerprint', on),
  },
  {
    id: 'show_type_monogram',
    label: 'Type monogram next to your name',
    description:
      'Compact MBTI · Enneagram label shown in feeds, sidebars, and activity.',
    icon: Tag,
    category: 'social',
    defaultEnabled: true,
    invalidateKeys: (userId) => [
      ['privacy-resonance-visibility', userId],
      ['privacy-show-monogram', userId],
    ],
    read: (uid) => readPrivacyFlag(uid, 'show_type_monogram', true),
    write: (uid, on) => writePrivacyFlag(uid, 'show_type_monogram', on),
  },
] as const;

export function getFeature(id: string): OptionalFeatureDefinition | undefined {
  return OPTIONAL_FEATURES.find((f) => f.id === id);
}

export function defaultsRecord(): Record<string, boolean> {
  return Object.fromEntries(OPTIONAL_FEATURES.map((f) => [f.id, f.defaultEnabled]));
}

export const GUEST_OPTIONAL_FEATURES_KEY = 'psyforge:optional_features';

export const CATEGORY_LABELS: Record<OptionalFeatureCategory, string> = {
  'personal-layer': 'Personal layers',
  social: 'Social visibility',
  discovery: 'Discovery',
};

export const CATEGORY_BLURBS: Record<OptionalFeatureCategory, string> = {
  'personal-layer':
    'Private modules that add a parallel lens. They never feed into your TPS profile.',
  social: 'Control how you appear next to your posts and across the community.',
  discovery: 'Help others find you for matching, mentorship, or collaboration.',
};
